import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import type { MouseEvent } from 'react';
import { company } from '~/company/Company';
import { IconButton } from '~/components/isolated/IconButton';
import { useGlobal } from '~/hooks/useGlobal';
import styles from './AddToCartButton.module.scss';

export type AddToCartButtonSize = 'm' | 's' | 'xs';

export interface AddToCartButtonProps {
  /**
   * Current price to display
   */
  currentPrice: string;

  /**
   * Old price (shown when there's a discount)
   */
  oldPrice?: string;

  /**
   * Size variant (m, s, xs)
   */
  size: AddToCartButtonSize;

  /**
   * Click handler
   */
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;

  /**
   * Disabled state
   */
  disabled?: boolean;

  /**
   * Additional CSS class
   */
  className?: string;

  /**
   * Hide price (show only button)
   */
  hidePrice?: boolean;

  /**
   * Product slug (for data-link attribute)
   */
  productSlug?: string;

  /**
   * Transparent state - card becomes transparent
   */
  transparent?: boolean;
}

/**
 * AddToCartButton - Component for initial product add to cart
 * Shows price + icon button, or just icon button if hidePrice is true
 */
const AddToCartButton = observer(
  ({
    currentPrice,
    oldPrice,
    size,
    onClick,
    disabled = false,
    className,
    hidePrice = false,
    productSlug,
    transparent = false,
  }: AddToCartButtonProps) => {
    const { isMobile } = useGlobal();

    const hasDiscount = Boolean(oldPrice && oldPrice !== currentPrice);
    const shouldShowPrice = size === 'm' && !hidePrice;

    const getIconButtonSize = () => {
      switch (size) {
        case 'm':
          return company.match({
            mcosmetic: isMobile ? 'M' : 'L',
            default: isMobile ? 'L' : 'XL',
          } as const);
        case 's':
          return isMobile ? 'XS' : 'L';
        case 'xs':
          return isMobile ? 'XS' : 'M';
      }
    };

    return (
      <div
        className={cn(styles.container, styles[`container_${size}`], className)}
      >
        {shouldShowPrice && (
          <div className={styles.priceColumn}>
            <span
              className={cn(
                styles.currentPrice,
                styles[`currentPrice_${size}`],
              )}
            >
              {currentPrice}
            </span>
            {hasDiscount && (
              <span className={cn(styles.oldPrice, styles[`oldPrice_${size}`])}>
                {oldPrice}
              </span>
            )}
          </div>
        )}

        <IconButton
          icon="plus-3.1"
          variant={company.match({
            mcosmetic: size === 'm' ? 'outline' : 'shadowXS',
            default: 'specialWhite',
          })}
          size={getIconButtonSize()}
          iconSize={
            isMobile
              ? size === 's'
                ? 10
                : size === 'xs'
                  ? 16
                  : 24
              : size === 'xs'
                ? 20
                : 24
          }
          onClick={onClick}
          disabled={disabled}
          aria-label="Add to cart"
          className={cn(
            styles.addButton,
            company.match({
              mcosmetic: transparent
                ? 'text-graphic-regular-brand!'
                : 'text-icon-primary!',
            }),
          )}
          data-link={
            productSlug ? `product-add-to-card-${productSlug}` : undefined
          }
        />
      </div>
    );
  },
);

export default AddToCartButton;
